// src/pages/Usuarios.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '../components/DataTable';
import ActivosAsignadosCell from '../components/ActivosAsignadosCell';
// eslint-disable-next-line no-unused-vars
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { Building, Edit, Trash2 } from 'lucide-react';
import { User, Users, TrendingUp, MapPin, ChevronLeft, ChevronRight, Plus, Eye } from '../components/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import Tooltip from '../components/Tooltip';
import { useToast } from '../contexts/ToastContext';
import EmpresaBadge from '../components/EmpresaBadge';
import UsuariosForm from './UsuariosForm';

// Función para obtener tickets activos (pendiente, asignado, en proceso)
const getTicketsActivos = (tickets) => {
    if (!Array.isArray(tickets)) return [];
    return tickets.filter(ticket =>
        ticket.estado === 'Pendiente' ||
        ticket.estado === 'Asignado' ||
        ticket.estado === 'En Proceso'
    );
};

// Indicador de Onboarding pendiente por usuario
function OnboardingBadge({ userId, email }) {
    const [pendingInfo, setPendingInfo] = useState(null);
    const [actasInfo, setActasInfo] = useState(null);
    const [ticketsInfo, setTicketsInfo] = useState(null);

    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();
        (async () => {
            try {
                const { data } = await api.get(`/onboarding/user/${userId}`, { signal: controller.signal });
                const parsed = (() => { try { return JSON.parse(data?.data || '{}'); } catch { return {}; } })();
                const stepKeys = ['rcp', 'cuentas', 'lic', 'equipo', 'valid', 'acta', 'cierre'];
                const done = stepKeys.filter(k => parsed.pasos && parsed.pasos[k]).length;
                const total = stepKeys.length;
                if (mounted) setPendingInfo({ done, total });
            } catch (e) {
                // Si no existe registro, es porque NO requiere onboarding (o no se marcó).
                // Null indica "no aplica/no mostrar badge".
                if (mounted) setPendingInfo(null);
            }

            // Consultar actas por email, si está disponible
            try {
                if (email) {
                    const { data: actas } = await api.get(`/actas/usuario/${encodeURIComponent(email)}`, { signal: controller.signal });
                    const arr = Array.isArray(actas) ? actas : [];
                    const pendientes = arr.filter(a => a.estado === 'Pendiente').length;
                    const rechazadas = arr.filter(a => a.estado === 'Rechazada').length;
                    const totalActas = arr.length;
                    if (mounted) setActasInfo({ pendientes, rechazadas, totalActas });
                } else {
                    if (mounted) setActasInfo({ pendientes: 0, rechazadas: 0, totalActas: 0 });
                }
            } catch {
                if (mounted) setActasInfo({ pendientes: 0, rechazadas: 0, totalActas: 0 });
            }

            // Consultar tickets por email, si está disponible
            try {
                if (email) {
                    const { data: tickets } = await api.get(`/tickets/usuario/${encodeURIComponent(email)}`, { signal: controller.signal });
                    const ticketsActivos = getTicketsActivos(tickets);
                    if (mounted) setTicketsInfo({ activos: ticketsActivos.length });
                } else {
                    if (mounted) setTicketsInfo({ activos: 0 });
                }
            } catch {
                if (mounted) setTicketsInfo({ activos: 0 });
            }
        })();
        return () => { mounted = false; controller.abort(); };
    }, [userId, email]);

    if (!pendingInfo) return null;
    const hasActas = !!actasInfo;
    const actasPend = hasActas ? actasInfo.pendientes : 0;
    const actasRech = hasActas ? actasInfo.rechazadas : 0;
    const actasTotal = hasActas ? actasInfo.totalActas : 0;
    const showWarnActa = actasRech > 0 || (actasTotal > 0 && actasPend > 0);
    const actaLabel = actasTotal === 0 ? 'Sin actas' : (actasRech > 0 ? `Rechazadas: ${actasRech}` : actasPend > 0 ? `Pendientes: ${actasPend}` : 'OK');
    const actaColor = actasTotal === 0 ? 'text-gray-300' : (actasRech > 0 ? 'text-red-300' : actasPend > 0 ? 'text-yellow-300' : 'text-green-300');

    // Obtener información de tickets
    const hasTickets = !!ticketsInfo;
    const ticketsActivos = hasTickets ? ticketsInfo.activos : 0;
    const showWarnTickets = hasTickets && ticketsActivos > 0;

    const badgeBg = actasRech > 0 ? 'bg-red-500' : ((pendingInfo && pendingInfo.done < pendingInfo.total) || actasPend > 0 || showWarnTickets) ? 'bg-yellow-500' : 'bg-green-500';

    // Ocultar sólo cuando: Onboarding completo, actas OK (tiene al menos una acta aprobada) y no hay tickets activos
    const actasOk = hasActas && actasTotal > 0 && actasPend === 0 && actasRech === 0;
    const ticketsOk = !hasTickets || ticketsActivos === 0;

    // Mostrar tooltip si hay problemas con onboarding, actas o tickets
    const onboardingPending = pendingInfo && pendingInfo.done < pendingInfo.total;
    const shouldShowTooltip = onboardingPending || showWarnActa || showWarnTickets;
    if (!shouldShowTooltip) return null;

    const actaHint = actasRech > 0 ? 'Acta rechazada: vuelve a emitir.' : (actasPend > 0 ? 'Acta pendiente de firma.' : '');

    const tooltipContent = (
        <div className="flex flex-col gap-0.5">
            {onboardingPending && (
                <>
                    <span><strong>Onboarding:</strong> {pendingInfo.done}/{pendingInfo.total}</span>
                    <span className="text-yellow-300">Completa el checklist para finalizar</span>
                </>
            )}
            {showWarnActa && (
                <>
                    <span className={'text-white'}><strong>Actas:</strong> {hasActas ? actaLabel : 'Verificando...'}</span>
                    {hasActas && actaHint && (
                        <span className="text-yellow-300">{actaHint}</span>
                    )}
                </>
            )}
            {showWarnTickets && (
                <>
                    <span className={'text-white'}><strong>Tickets:</strong> {ticketsActivos} activo{ticketsActivos !== 1 ? 's' : ''}</span>
                    <span className={'text-yellow-300'}>Tickets pendientes de resolución</span>
                </>
            )}
        </div>
    );

    return (
        <Tooltip content={tooltipContent}>
            <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${badgeBg} text-white text-[10px] font-bold`}>!</span>
        </Tooltip>
    );
}

const columnHelper = createColumnHelper()

const columns = [
    columnHelper.accessor(row => `${row.nombre} ${row.apellido}`.trim(), {
        header: 'Nombre Completo',
        cell: ({ getValue, row }) => (
            <div className="flex items-center gap-2">
                {/* Luz de estado (Verde = activo, Rojo = inactivo) */}
                <span
                    className={`w-3 h-3 rounded-full flex-shrink-0 ${row.original.activo ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]'}`}
                    title={row.original.activo ? 'Activo' : 'Inactivo'}
                ></span>
                <span className={row.original.activo ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-400 dark:text-gray-500 italic'}>
                    {getValue()}
                </span>
                <EmpresaBadge empresa={row.original.empresa} />
                <OnboardingBadge userId={row.original.id} email={row.original.email} />
            </div>
        )
    }),
    columnHelper.accessor('username', {
        header: 'Usuario',
        cell: ({ getValue }) => (
            <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
                {getValue() || 'N/A'}
            </span>
        )
    }),
    columnHelper.accessor('rut', { header: 'RUT' }),
    columnHelper.accessor('email', { header: 'Email' }),
    columnHelper.accessor('departamento', { header: 'Departamento' }),
    columnHelper.accessor('cargo', { header: 'Cargo' }),
    columnHelper.accessor(
        row => row.activosAsignados,
        {
            id: 'activosAsignados',
            header: 'Activos Asignados',
            enableSorting: false,
            enableColumnFilter: false,
            cell: ({ getValue, row }) => <ActivosAsignadosCell activosAsignados={getValue()} usuario={row.original} />
        }
    ),
]

export default function Usuarios() {
    const navigate = useNavigate()
    const location = useLocation()
    const { showToast } = useToast()
    const [usuarios, setUsuarios] = useState([])
    const [loading, setLoading] = useState(true)
    const [rowSelection, setRowSelection] = useState({})

    // Estados para navegación de cards
    const [currentDeptIndex, setCurrentDeptIndex] = useState(0)
    const [currentEmpresaIndex, setCurrentEmpresaIndex] = useState(0)
    const [currentActivosIndex, setCurrentActivosIndex] = useState(0)

    // Estados para filtrado
    // Estados para filtrado
    const [activeFilter, setActiveFilter] = useState(location.state?.activeFilter || null)

    // Persistir estado en navegación
    useEffect(() => {
        const currentState = { activeFilter }
        const previousState = location.state

        if (JSON.stringify(previousState?.activeFilter) !== JSON.stringify(activeFilter)) {
            navigate('.', { replace: true, state: currentState })
        }
    }, [activeFilter, navigate, location.state])

    // Filtrado centralizado con useMemo
    const filteredData = useMemo(() => {
        if (!activeFilter) return usuarios

        switch (activeFilter.type) {
            case 'departamento':
                return usuarios.filter(user => user.departamento === activeFilter.value)
            case 'empresa':
                return usuarios.filter(user => {
                    const userEmpresa = user.empresa && user.empresa.trim() !== '' ? user.empresa : 'Sin empresa'
                    return userEmpresa === activeFilter.value
                })
            case 'withAssets':
                return usuarios.filter(user => user.activosAsignados && user.activosAsignados.length > 0)
            case 'withoutAssets':
                return usuarios.filter(user => !user.activosAsignados || user.activosAsignados.length === 0)
            case 'activos':
                return usuarios.filter(user => user.activo === true)
            case 'inactivos':
                return usuarios.filter(user => user.activo === false)
            default:
                return usuarios
        }
    }, [usuarios, activeFilter])

    // Estados para el modal de confirmación
    // eslint-disable-next-line no-unused-vars
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    // eslint-disable-next-line no-unused-vars
    const [usuariosToDelete, setUsuariosToDelete] = useState([])

    // (el flujo de creación navegará a UsuariosForm con query param onboarding)

    const [formNuevo, setFormNuevo] = useState(false)
    const [formEditarId, setFormEditarId] = useState(null)

    // El menú lateral entra con ?nuevo=1: abre el formulario de alta como modal sobre el listado
    useEffect(() => {
        if (new URLSearchParams(location.search).get('nuevo') === '1') {
            setFormNuevo(true)
            navigate(location.pathname, { replace: true, state: location.state })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search])

    const fetchUsuarios = async () => {
        setLoading(true)
        try {
            const { data } = await api.get('/usuarios')
            setUsuarios(data)
            // setFilteredData(data) // Ya no es necesario con useMemo
        } catch (err) {
            console.error('Error al cargar usuarios:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchUsuarios()
    }, [])

    // Sincronizar filtros desde la URL al navegar desde el sidebar
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const filtro = searchParams.get('filtro');
        const emp = searchParams.get('empresa');
        const depto = searchParams.get('departamento');

        if (filtro === 'activos') {
            setActiveFilter({ type: 'activos' });
        } else if (filtro === 'inactivos' || filtro === 'desvinculados') {
            setActiveFilter({ type: 'inactivos' });
        } else if (filtro === 'withAssets' || filtro === 'conActivos') {
            setActiveFilter({ type: 'withAssets' });
        } else if (filtro === 'withoutAssets' || filtro === 'sinActivos') {
            setActiveFilter({ type: 'withoutAssets' });
        } else if (filtro === 'all') {
            setActiveFilter(null);
        } else if (emp) {
            setActiveFilter({ type: 'empresa', value: emp });
        } else if (depto) {
            setActiveFilter({ type: 'departamento', value: depto });
        }
    }, [location.search]);

    // Calcular estadísticas
    const stats = useMemo(() => {
        if (!usuarios.length) return {
            total: 0,
            porEmpresa: {},
            porDepartamento: {},
            usuariosConActivos: 0,
            usuariosSinActivos: 0,
            departamentosArray: [],
            empresasArray: [],
            activosArray: [],
            activosStatus: { activos: 0, inactivos: 0 }
        }

        const porEmpresa = usuarios.reduce((acc, user) => {
            // Manejar casos donde empresa es null, undefined, o string vacío
            const empresa = user.empresa && user.empresa.trim() !== '' ? user.empresa : 'Sin empresa'
            acc[empresa] = (acc[empresa] || 0) + 1
            return acc
        }, {})

        const porDepartamento = usuarios.reduce((acc, user) => {
            const depto = user.departamento || 'Sin departamento'
            acc[depto] = (acc[depto] || 0) + 1
            return acc
        }, {})

        const usuariosConActivos = usuarios.filter(user =>
            user.activosAsignados && user.activosAsignados.length > 0
        ).length

        const usuariosSinActivos = usuarios.length - usuariosConActivos

        const departamentosArray = Object.entries(porDepartamento)
            .sort(([, a], [, b]) => b - a)
            .map(([nombre, cantidad]) => ({ nombre, cantidad }))

        const empresasArray = Object.entries(porEmpresa)
            .sort(([, a], [, b]) => b - a)
            .map(([nombre, cantidad]) => ({ nombre, cantidad }))

        const activosArray = [
            { nombre: 'Con Activos', cantidad: usuariosConActivos },
            { nombre: 'Sin Activos', cantidad: usuariosSinActivos }
        ]

        return {
            total: usuarios.length,
            porEmpresa,
            porDepartamento,
            usuariosConActivos,
            usuariosSinActivos,
            departamentosArray,
            empresasArray,
            activosArray,
            activosStatus: {
                activos: usuarios.filter(u => u.activo).length,
                inactivos: usuarios.filter(u => !u.activo).length
            }
        }
    }, [usuarios])

    // Resetear índices cuando cambien los datos
    useEffect(() => {
        setCurrentDeptIndex(0)
        setCurrentEmpresaIndex(0)
        setCurrentActivosIndex(0)
    }, [stats.departamentosArray.length, stats.empresasArray.length, stats.activosArray.length])

    // Funciones de filtrado simplificadas
    const handleFilterByDepartment = (departamento) => {
        if (activeFilter?.type === 'departamento' && activeFilter?.value === departamento) {
            setActiveFilter(null)
        } else {
            setActiveFilter({ type: 'departamento', value: departamento })
        }
    }

    const handleFilterByCompany = (empresa) => {
        if (activeFilter?.type === 'empresa' && activeFilter?.value === empresa) {
            setActiveFilter(null)
        } else {
            setActiveFilter({ type: 'empresa', value: empresa })
        }
    }

    const handleFilterByStatus = (status) => {
        if (activeFilter?.type === status) {
            setActiveFilter(null)
        } else {
            setActiveFilter({ type: status })
        }
    }

    const handleFilterByTotal = () => {
        if (!activeFilter) {
            setActiveFilter({ type: 'activos' })
        } else if (activeFilter.type === 'activos') {
            setActiveFilter({ type: 'inactivos' })
        } else if (activeFilter.type === 'inactivos') {
            setActiveFilter(null)
        } else {
            setActiveFilter(null)
        }
    }

    const handleFilterByActivos = (tipo) => {
        if (activeFilter?.type === tipo) {
            setActiveFilter(null)
        } else {
            setActiveFilter({ type: tipo })
        }
    }

    const selectedRowIndex = Object.keys(rowSelection)[0]
    const selectedId = selectedRowIndex !== undefined ? filteredData[selectedRowIndex]?.id : undefined
    const selectedCount = Object.keys(rowSelection).length

    const handleDelete = () => {
        if (selectedCount === 0) return

        // Obtener información de los usuarios seleccionados
        const usuariosSeleccionados = Object.keys(rowSelection).map(index => filteredData[parseInt(index)])

        // Preparar datos para el modal
        const usuariosParaEliminar = usuariosSeleccionados.map(user => ({
            id: user.id,
            nombre: user.nombre,
            apellido: user.apellido,
            departamento: user.departamento,
            activosAsignados: user.activosAsignados ? user.activosAsignados.length : 0
        }))

        setUsuariosToDelete(usuariosParaEliminar)
        setShowDeleteModal(true)
    }

    // eslint-disable-next-line no-unused-vars
    const handleConfirmDelete = async () => {
        try {
            if (usuariosToDelete.length === 1) {
                // Eliminación individual
                const response = await api.delete(`/usuarios/${usuariosToDelete[0].id}`)

                // Mostrar información adicional si hay activos desasignados
                if (response.data && response.data.activosDesasignados > 0) {
                    showToast(`Usuario eliminado. ${response.data.activosDesasignados} activo(s) desasignados automáticamente.`, 'success')
                } else {
                    showToast('Usuario eliminado exitosamente.', 'success')
                }
            } else {
                // Eliminación múltiple
                const idsAEliminar = usuariosToDelete.map(u => u.id)
                const response = await api.delete('/usuarios/multiple', { data: idsAEliminar })

                // Mostrar información adicional si hay activos desasignados
                if (response.data && response.data.totalActivosDesasignados > 0) {
                    showToast(`${response.data.usuariosEliminados} usuario(s) eliminados. ${response.data.totalActivosDesasignados} activo(s) desasignados automáticamente.`, 'success')
                } else {
                    showToast(`${response.data.usuariosEliminados} usuario(s) eliminados exitosamente.`, 'success')
                }
            }

            await fetchUsuarios() // refresca la lista desde el backend
            setRowSelection({})
            setShowDeleteModal(false)
            setUsuariosToDelete([])
        } catch (err) {
            console.error('Error al eliminar usuario(s):', err)
            showToast('Error al eliminar usuario(s). Por favor, inténtalo de nuevo.', 'error')
        }
    }

    // eslint-disable-next-line no-unused-vars
    const handleCancelDelete = () => {
        setShowDeleteModal(false)
        setUsuariosToDelete([])
    }

    const handleRowClick = (row) => {
        // Navegar al detalle del usuario
        navigate(`/usuarios/${row.id}`)
    }

    const actions = (
        <div className="flex flex-wrap items-center gap-2">
            <button
                onClick={() => setFormNuevo(true)}
                className="flex items-center space-x-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 rounded-lg transition-colors font-medium border border-transparent"
            >
                <Plus className="w-3.5 h-3.5" />
                <span>Nuevo</span>
            </button>
            <button
                onClick={() => navigate(`/usuarios/${selectedId}`)}
                disabled={!selectedId || selectedCount !== 1}
                className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedId && selectedCount === 1
                    ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    }`}
            >
                <Eye className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Ver</span>
                <span className="sm:hidden">Ver</span>
            </button>
            <button
                onClick={() => setFormEditarId(selectedId)}
                disabled={!selectedId || selectedCount !== 1}
                className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedId && selectedCount === 1
                    ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    }`}
            >
                <Edit className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Editar</span>
                <span className="sm:hidden">Edit</span>
            </button>
            <button
                onClick={handleDelete}
                disabled={selectedCount === 0}
                className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedCount > 0
                    ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    }`}
            >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{selectedCount === 1 ? 'Borrar' : `Borrar (${selectedCount})`}</span>
                <span className="sm:hidden">{selectedCount === 1 ? 'Borrar' : `(${selectedCount})`}</span>
            </button>
        </div>
    )

    if (loading) {
        return (
            <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300">Cargando usuarios…</p>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-semibold flex items-center text-gray-900 dark:text-white">
                <User className="w-6 h-6 mr-2 text-primary" />
                Usuarios
            </h1>

            {/* Cards de estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total de usuarios */}
                <div
                    className={`relative bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105 border border-transparent ${activeFilter === null ? 'ring-2 ring-blue-500' : ''} ${activeFilter?.type === 'activos' || activeFilter?.type === 'inactivos' ? `ring-2 ${activeFilter?.type === 'inactivos' ? 'ring-red-500' : 'ring-green-500'}` : ''}`}
                    onClick={handleFilterByTotal}
                >
                    {/* Indicador de estado en la esquina superior derecha */}
                    <div className="absolute top-3 right-3">
                        <span
                            className={`w-3 h-3 rounded-full shadow-sm block ${activeFilter?.type === 'inactivos' ? 'bg-red-500 animate-pulse' : (activeFilter?.type === 'activos' ? 'bg-green-500' : 'bg-blue-400')}`}
                            title={activeFilter?.type === 'inactivos' ? 'Filtrando: Inactivos' : activeFilter?.type === 'activos' ? 'Filtrando: Activos' : 'Filtro: Todos'}
                        />
                    </div>

                    <div className="p-3 rounded-lg text-blue-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                        <Users className="h-6 w-6" />
                    </div>
                    <div className="flex-1 pr-6">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Total {activeFilter?.type === 'inactivos' ? '(Inactivos)' : activeFilter?.type === 'activos' ? '(Activos)' : 'Usuarios'}
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {activeFilter?.type === 'inactivos'
                                ? stats.activosStatus.inactivos
                                : activeFilter?.type === 'activos'
                                    ? stats.activosStatus.activos
                                    : stats.total}
                        </p>
                    </div>
                </div>

                {/* Activos - Card interactivo */}
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-transparent shadow-sm hover:shadow-md transition-all hover:scale-105">
                    <div className="flex items-center justify-between h-full">
                        <div
                            className="flex items-center gap-4 cursor-pointer flex-1"
                            onClick={() => {
                                if (stats.activosArray.length > 0) {
                                    const tipo = stats.activosArray[currentActivosIndex]?.nombre === 'Con Activos' ? 'withAssets' : 'withoutAssets'
                                    handleFilterByActivos(tipo)
                                }
                            }}
                        >
                            <div className="p-3 rounded-lg text-emerald-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <TrendingUp className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Activos</p>
                                {stats.activosArray.length > 0 ? (
                                    <>
                                        <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                            {stats.activosArray[currentActivosIndex]?.nombre || 'N/A'}
                                        </p>
                                        <div className="flex flex-col text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            <span>{stats.activosArray[currentActivosIndex]?.cantidad || 0} usuarios</span>
                                            <span>{stats.total > 0 ? `${Math.round((stats.activosArray[currentActivosIndex]?.cantidad / stats.total) * 100)}%` : '0%'}</span>
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Sin datos</p>
                                )}
                            </div>
                        </div>
                        {stats.activosArray.length > 1 && (
                            <div className="flex flex-col space-y-1">
                                <button
                                    onClick={() => setCurrentActivosIndex(prev => prev === 0 ? stats.activosArray.length - 1 : prev - 1)}
                                    className="p-1 rounded bg-white/60 hover:bg-white dark:bg-black/20 dark:hover:bg-black/40 text-emerald-700 dark:text-emerald-400 transition-colors"
                                >
                                    <ChevronLeft className="h-3 w-3" />
                                </button>
                                <button
                                    onClick={() => setCurrentActivosIndex(prev => prev === stats.activosArray.length - 1 ? 0 : prev + 1)}
                                    className="p-1 rounded bg-white/60 hover:bg-white dark:bg-black/20 dark:hover:bg-black/40 text-emerald-700 dark:text-emerald-400 transition-colors"
                                >
                                    <ChevronRight className="h-3 w-3" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Departamentos - Card interactivo */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-transparent shadow-sm hover:shadow-md transition-all hover:scale-105">
                    <div className="flex items-center justify-between h-full">
                        <div
                            className="flex items-center gap-4 cursor-pointer flex-1"
                            onClick={() => {
                                if (stats.departamentosArray.length > 0) {
                                    handleFilterByDepartment(stats.departamentosArray[currentDeptIndex]?.nombre)
                                }
                            }}
                        >
                            <div className="p-3 rounded-lg text-purple-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <MapPin className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Depto</p>
                                {stats.departamentosArray.length > 0 ? (
                                    <>
                                        <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight truncate">
                                            {stats.departamentosArray[currentDeptIndex]?.nombre || 'N/A'}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {stats.departamentosArray[currentDeptIndex]?.cantidad || 0} usuarios
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Sin datos</p>
                                )}
                            </div>
                        </div>
                        {stats.departamentosArray.length > 1 && (
                            <div className="flex flex-col space-y-1">
                                <button
                                    onClick={() => setCurrentDeptIndex(prev => prev === 0 ? stats.departamentosArray.length - 1 : prev - 1)}
                                    className="p-1 rounded bg-white/60 hover:bg-white dark:bg-black/20 dark:hover:bg-black/40 text-purple-700 dark:text-purple-400 transition-colors"
                                >
                                    <ChevronLeft className="h-3 w-3" />
                                </button>
                                <button
                                    onClick={() => setCurrentDeptIndex(prev => prev === stats.departamentosArray.length - 1 ? 0 : prev + 1)}
                                    className="p-1 rounded bg-white/60 hover:bg-white dark:bg-black/20 dark:hover:bg-black/40 text-purple-700 dark:text-purple-400 transition-colors"
                                >
                                    <ChevronRight className="h-3 w-3" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Empresas - Card interactivo */}
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 border border-transparent shadow-sm hover:shadow-md transition-all hover:scale-105">
                    <div className="flex items-center justify-between h-full">
                        <div
                            className="flex items-center gap-4 cursor-pointer flex-1"
                            onClick={() => {
                                if (stats.empresasArray.length > 0) {
                                    handleFilterByCompany(stats.empresasArray[currentEmpresaIndex]?.nombre)
                                }
                            }}
                        >
                            <div className="p-3 rounded-lg text-orange-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <Building className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Empresa</p>
                                {stats.empresasArray.length > 0 ? (
                                    <>
                                        <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight truncate">
                                            {stats.empresasArray[currentEmpresaIndex]?.nombre || 'N/A'}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {stats.empresasArray[currentEmpresaIndex]?.cantidad || 0} usuarios
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Sin empresas</p>
                                )}
                            </div>
                        </div>
                        {stats.empresasArray.length > 1 && (
                            <div className="flex flex-col space-y-1">
                                <button
                                    onClick={() => setCurrentEmpresaIndex(prev => prev === 0 ? stats.empresasArray.length - 1 : prev - 1)}
                                    className="p-1 rounded bg-white/60 hover:bg-white dark:bg-black/20 dark:hover:bg-black/40 text-orange-700 dark:text-orange-400 transition-colors"
                                >
                                    <ChevronLeft className="h-3 w-3" />
                                </button>
                                <button
                                    onClick={() => setCurrentEmpresaIndex(prev => prev === stats.empresasArray.length - 1 ? 0 : prev + 1)}
                                    className="p-1 rounded bg-white/60 hover:bg-white dark:bg-black/20 dark:hover:bg-black/40 text-orange-700 dark:text-orange-400 transition-colors"
                                >
                                    <ChevronRight className="h-3 w-3" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Indicador de filtro activo */}
            {activeFilter && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                        <strong>Filtro activo:</strong> {activeFilter.type === 'departamento' ? 'Departamento' :
                            activeFilter.type === 'empresa' ? 'Empresa' :
                                activeFilter.type === 'withAssets' ? 'Usuarios con activos' :
                                    activeFilter.type === 'withoutAssets' ? 'Usuarios sin activos' : ''} - {activeFilter.value || (activeFilter.type === 'withAssets' ? 'Con activos' : activeFilter.type === 'withoutAssets' ? 'Sin activos' : '')}
                        <button
                            onClick={() => {
                                setActiveFilter(null)
                            }}
                            className="ml-2 text-blue-500 hover:text-blue-700 underline"
                        >
                            Limpiar filtro
                        </button>
                    </p>
                </div>
            )}

            <DataTable
                columns={columns}
                data={filteredData}
                enableRowSelection
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                onRowClick={handleRowClick}
                actions={actions}
            />

            {/* Modal de confirmación de eliminación */}
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                usuarios={usuariosToDelete}
                activosDesasignados={usuariosToDelete}
            />

            {formEditarId && (
                <UsuariosForm modal edit idProp={formEditarId} onClose={() => setFormEditarId(null)} onSaved={() => fetchUsuarios()} />
            )}
            {formNuevo && (
                <UsuariosForm modal onClose={() => setFormNuevo(false)} onSaved={() => fetchUsuarios()} />
            )}
        </div>
    );
}
